import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, orderBy, onSnapshot, where } from 'firebase/firestore';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import './ReceiptHistory.css';

const ReceiptHistory = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [filterName, setFilterName] = useState('');
  const [filterReferenceNumber, setFilterReferenceNumber] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    if (sessionStorage.getItem('receipt_auth_pin') === 'rl090692') {
      setIsAuthenticated(true);
    } else {
      window.location.href = '/receipt';
    }
  }, []);

  useEffect(() => {
    // We start with a base query ordered by date descending
    let q = query(
      collection(db, 'receipts'),
      orderBy('date', 'desc')
    );

    // Apply filters if they exist
    if (filterName) {
      // Basic prefix search (case-sensitive as per standard Firestore)
      q = query(q,
        where('clientName', '>=', filterName),
        where('clientName', '<=', filterName + '\uf8ff')
      );
    }

    if (startDate) {
      // Start of the selected day
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      q = query(q, where('date', '>=', start));
    }

    if (endDate) {
      // End of the selected day
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      q = query(q, where('date', '<=', end));
    }

    const unsubscribe = onSnapshot(q, (snapshot) => {
      let results = [];
      snapshot.forEach((doc) => {
        results.push({ id: doc.id, ...doc.data() });
      });

      // Client-side filter for reference number to avoid hitting Firestore composite index limits
      if (filterReferenceNumber) {
        results = results.filter(r => r.referenceNumber && r.referenceNumber.includes(filterReferenceNumber));
      }

      setReceipts(results);
      setLoading(false);
      setError(null);
    }, (err) => {
      console.error("Error fetching receipts:", err);
      // Handle the complex query missing index error here
      if (err.message.includes('requires an index')) {
        const urlMatch = err.message.match(/(https:\/\/console\.firebase\.google\.com[^\s]+)/);
        if (urlMatch) {
          setError(
            <span>
              נדרש לבנות אינדקס במסד הנתונים כדי לסנן לפי כל השדות יחד. <br />
              <a href={urlMatch[1]} target="_blank" rel="noopener noreferrer">
                לחץ כאן כדי לבנות את האינדקס אוטומטית (פעולה חד פעמית)
              </a>
            </span>
          );
        } else {
          setError('שגיאה בהבאת נתונים. אם הפעלת מסננים מרובים, ייתכן שחסר אינדקס ב-Firebase.');
        }
      } else {
        setError('שגיאה בטעינת הקבלות. אנא בדוק את חיבור הרשת.');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [filterName, startDate, endDate, filterReferenceNumber]);

  const handleDownload = (url) => {
    window.open(url, '_blank');
  };

  const handleLogout = () => {
    sessionStorage.removeItem('receipt_auth_pin');
    window.location.href = '/receipt';
  };

  const openModal = (receipt) => {
    setSelectedReceipt(receipt);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedReceipt(null);
  };

  const handleExportToExcel = async () => {
    if (receipts.length === 0) {
      alert('אין נתונים לייצוא');
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('קבלות', {
      views: [{ rightToLeft: true }]
    });

    // Define main columns
    worksheet.columns = [
      { header: "מספר קבלה", key: "receiptNumber", width: 15 },
      { header: "תאריך הפקה", key: "date", width: 15 },
      { header: "שעה", key: "time", width: 10 },
      { header: "שם משלם", key: "clientName", width: 25 },
      { header: "סכום (₪)", key: "amount", width: 12 },
      { header: "אמצעי תשלום", key: "paymentMethod", width: 15 },
      { header: "מספר אסמכתא", key: "referenceNumber", width: 15 },
      { header: "אימייל משלם", key: "clientEmail", width: 25 },
      { header: "פירוט פריטים", key: "itemsSummary", width: 40 }
    ];

    // Style the header row (Blue background, White text)
    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF1E40AF' } // Professional Dark Blue (#1e40af)
    };
    headerRow.alignment = { vertical: 'middle', horizontal: 'center' };

    // Fill data
    receipts.forEach(receipt => {
      let itemsSummary = "";
      if (receipt.items && receipt.items.length > 0) {
        itemsSummary = receipt.items.map(item => `${item.lessonType} (כמות: ${item.quantity})`).join(', ');
      } else if (receipt.lessonType) {
        // Fallback for older receipts logic (root level lessonType)
        itemsSummary = `${receipt.lessonType} (כמות: ${receipt.quantity || 1})`;
      } else if (receipt.description) {
        // Extra fallback just in case
        itemsSummary = `${receipt.description} (כמות: ${receipt.quantity || 1})`;
      }

      const rowData = {
        receiptNumber: receipt.receiptNumber || '',
        date: receipt.dateString
          ? receipt.dateString.split('-').reverse().join('/')
          : (receipt.date && receipt.date.toDate
            ? receipt.date.toDate().toLocaleDateString('he-IL')
            : ''),
        time: receipt.time || '',
        clientName: receipt.clientName || '',
        amount: Number(receipt.amount) || 0,
        paymentMethod: receipt.paymentMethod || '',
        referenceNumber: receipt.referenceNumber || '',
        clientEmail: receipt.clientEmail || '',
        itemsSummary: itemsSummary
      };

      worksheet.addRow(rowData);
    });

    // Add a Total row
    const totalAmount = receipts.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
    const totalRow = worksheet.addRow({
      clientName: 'סה"כ הכנסות בסינון:',
      amount: totalAmount
    });
    totalRow.font = { bold: true };
    totalRow.getCell('amount').numFmt = '₪#,##0.00';

    // Set numbers format for amount column (all rows except header)
    worksheet.getColumn('amount').numFmt = '₪#,##0.00';

    // Generate buffer and download
    const buffer = await workbook.xlsx.writeBuffer();
    const dateStr = new Date().toLocaleDateString('he-IL').replace(/\./g, '-');
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `קבלות_מס_הכנסה_${dateStr}.xlsx`);
  };

  if (!isAuthenticated) return null;

  return (
    <div className="receipt-history-container" dir="rtl">
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '15px' }}>
        <h2 style={{ margin: 0 }}>היסטוריית קבלות (מס הכנסה)</h2>
        <button onClick={handleLogout} className="clear-filter-btn" style={{ backgroundColor: '#f56565', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' }}>התנתק (Sign Out)</button>
      </div>

      <div className="history-filters">
        <input
          type="text"
          placeholder="חיפוש לפי שם לקוח..."
          value={filterName}
          onChange={(e) => setFilterName(e.target.value)}
        />
        <input
          type="text"
          placeholder="חיפוש לפי מספר אסמכתא..."
          value={filterReferenceNumber}
          onChange={(e) => setFilterReferenceNumber(e.target.value)}
        />
        <div className="date-filters">
          <label>מתאריך:</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <label>עד תאריך:</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="clear-filter-btn" onClick={() => {
            setFilterName(''); setStartDate(''); setEndDate(''); setFilterReferenceNumber('');
          }}>נקה סינון</button>
          
          <button 
            onClick={handleExportToExcel}
            className="clear-filter-btn" 
            style={{ backgroundColor: '#2e7d32', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
          >
            ייצוא לאקסל
          </button>
        </div>
      </div>

      {error && <div className="history-error-banner">{error}</div>}

      <div className="table-responsive">
        <table className="history-table">
          <thead>
            <tr>
              <th>מס' קבלה</th>
              <th>תאריך הפקה</th>
              <th>שעה</th>
              <th>שם משלם</th>
              <th>סכום</th>
              <th>אמצעי תשלום</th>
              <th>פעולות</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7" className="text-center">טוען קבלות...</td></tr>
            ) : receipts.length === 0 ? (
              <tr><td colSpan="7" className="text-center">לא נמצאו קבלות מתאימות</td></tr>
            ) : (
              receipts.map(receipt => (
                <tr key={receipt.id} onClick={() => openModal(receipt)} className="clickable-row">
                  <td>{receipt.receiptNumber}</td>
                  <td>
                    {receipt.dateString
                      ? receipt.dateString.split('-').reverse().join('/')
                      : (receipt.date && receipt.date.toDate
                        ? receipt.date.toDate().toLocaleDateString('he-IL')
                        : 'N/A')
                    }
                  </td>
                  <td>{receipt.time || 'N/A'}</td>
                  <td>{receipt.clientName}</td>
                  <td>₪{receipt.amount}</td>
                  <td>{receipt.paymentMethod}</td>
                  <td className="actions-cell">
                    <button
                      className="download-btn-enhanced"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(receipt.fileUrl);
                      }}
                    >
                      <span className="btn-icon">📄</span>
                      <span className="btn-text">הורד PDF</span>
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Details Modal */}
      {isModalOpen && selectedReceipt && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>קבלה {selectedReceipt.receiptNumber}</h3>
              <button className="close-modal-btn" onClick={closeModal}>&times;</button>
            </div>
            
            <div className="modal-body">
              <div className="receipt-summary-grid">
                <div className="summary-item">
                  <span className="label">שם לקוח:</span>
                  <span className="value">{selectedReceipt.clientName}</span>
                </div>
                <div className="summary-item">
                  <span className="label">תאריך:</span>
                  <span className="value">
                    {selectedReceipt.dateString 
                      ? selectedReceipt.dateString.split('-').reverse().join('/') 
                      : (selectedReceipt.date?.toDate ? selectedReceipt.date.toDate().toLocaleDateString('he-IL') : 'N/A')}
                  </span>
                </div>
                <div className="summary-item">
                  <span className="label">שעה:</span>
                  <span className="value">{selectedReceipt.time || 'N/A'}</span>
                </div>
                <div className="summary-item">
                  <span className="label">אמצעי תשלום:</span>
                  <span className="value">{selectedReceipt.paymentMethod}</span>
                </div>
                {selectedReceipt.referenceNumber && (
                  <div className="summary-item">
                    <span className="label">מס' אסמכתא:</span>
                    <span className="value">{selectedReceipt.referenceNumber}</span>
                  </div>
                )}
                <div className="summary-item total-summary">
                  <span className="label">סה"כ לתשלום:</span>
                  <span className="value">₪{selectedReceipt.amount}</span>
                </div>
              </div>

              <h4>פירוט פריטים</h4>
              <div className="modal-items-table-container">
                <table className="modal-items-table">
                  <thead>
                    <tr>
                      <th>תיאור</th>
                      <th>כמות</th>
                      <th>מחיר ליחידה</th>
                      <th>סה"כ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedReceipt.items && selectedReceipt.items.length > 0) ? (
                      selectedReceipt.items.map((item, idx) => (
                        <tr key={idx}>
                          <td>{item.lessonType}</td>
                          <td>{item.quantity}</td>
                          <td>₪{item.price}</td>
                          <td>₪{item.price * item.quantity}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td>{selectedReceipt.lessonType || selectedReceipt.description || 'ללא תיאור'}</td>
                        <td>{selectedReceipt.quantity || 1}</td>
                        <td>₪{selectedReceipt.amount / (selectedReceipt.quantity || 1)}</td>
                        <td>₪{selectedReceipt.amount}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="modal-footer">
              <button className="download-btn-full" onClick={() => handleDownload(selectedReceipt.fileUrl)}>
                📥 הורד קובץ PDF המקורי
              </button>
              <button className="close-btn-secondary" onClick={closeModal}>סגור</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReceiptHistory;
